import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ulid } from 'ulid';

export interface TaskType {
  id: string;
  name: string;
  memo: string;
  languageDependent: boolean; // 언어 종속성 여부
  icon: string;
  order: number; // 표시 순서
  isActive: boolean; // 활성 상태
  color?: string; // 색상
  description?: string; // 설명
  editorOptions?: any; // 에디터 옵션
  workerDisplayName?: string; // 작업자 표시 이름
  workerDisplayDescription?: string; // 작업자 표시 설명
  tags?: string[]; // 태그 (예: abc, 분야:기술)
}

interface TaskTypeStore {
  taskTypes: TaskType[];
  filteredTaskTypes: TaskType[];
  searchQuery: string;
  isLoading: boolean;

  // Actions
  loadTaskTypes: () => Promise<void>;
  setSearchQuery: (query: string) => void;
  filterTaskTypes: () => void;
  addTaskType: (taskType: Omit<TaskType, 'id'>) => void;
  updateTaskType: (id: string, updates: Partial<TaskType>) => void;
  deleteTaskType: (id: string) => void;
  getTaskTypeById: (id: string) => TaskType | undefined;
}

// 기본 데이터
const defaultTaskTypes: TaskType[] = [
  {
    id: '01JGHPR8BL4MKFRQ2EVTXSYQ9R',
    name: '전사',
    memo: '음성/영상을 텍스트로 변환하는 작업',
    languageDependent: false, // 언어 종속성 없음
    icon: 'FileText',
    order: 1,
    isActive: true
  },
  {
    id: '01JGHPR9DN7PKFRN4FVTXSYQ1S',
    name: '전사검수',
    memo: '전사된 텍스트의 정확성을 검토하는 작업',
    languageDependent: false, // 언어 종속성 없음
    icon: 'Search',
    order: 2,
    isActive: true
  },
  {
    id: '01JGHPRAFP9QKFR6GVTXSYQ2T',
    name: '번역',
    memo: '소스 언어를 타겟 언어로 번역하는 작업',
    languageDependent: true, // 언어 종속성 있음
    icon: 'Globe',
    order: 3,
    isActive: true
  },
  {
    id: '01JGHPRBHR2RJKF8HVTXSYQ3U',
    name: '번역검수',
    memo: '번역된 텍스트의 품질을 검토하는 작업',
    languageDependent: true, // 언어 종속성 있음
    icon: 'CheckCircle',
    order: 4,
    isActive: true
  },
  {
    id: '01JGHPRCJT5SKFR9JVTXSYQ4V',
    name: 'PM 확인',
    memo: '프로젝트 매니저의 최종 검토 작업',
    languageDependent: true, // 언어 종속성 있음
    icon: 'User',
    order: 5,
    isActive: true
  },
  {
    id: '01JGHPRDLV8TKFRB2WVTXSYQ5W',
    name: '렌더링',
    memo: '최종 결과물을 렌더링하는 작업',
    languageDependent: false, // 언어 종속성 없음
    icon: 'Video',
    order: 6,
    isActive: true
  }
];

export const useTaskTypeStore = create<TaskTypeStore>()(
  devtools(
    (set, get) => ({
      taskTypes: [...defaultTaskTypes],
      filteredTaskTypes: [...defaultTaskTypes],
      searchQuery: '',
      isLoading: false,

      loadTaskTypes: async () => {
        const { taskTypes } = get();
        
        // 이미 데이터가 있으면 다시 로드하지 않음
        if (taskTypes.length > 0) {
          console.log('TaskTypeStore: Data already loaded, skipping reload');
          return;
        }
        
        console.log('TaskTypeStore: Loading default task types...');
        set({ isLoading: true });
        // 실제 환경에서는 API 호출
        await new Promise(resolve => setTimeout(resolve, 300));
        
        set({ 
          taskTypes: [...defaultTaskTypes], 
          filteredTaskTypes: [...defaultTaskTypes],
          isLoading: false 
        });
        console.log('TaskTypeStore: Default task types loaded');
      },

      setSearchQuery: (query) => {
        set({ searchQuery: query });
        get().filterTaskTypes();
      },

      filterTaskTypes: () => {
        const { taskTypes, searchQuery } = get();
        
        if (!searchQuery.trim()) {
          set({ filteredTaskTypes: taskTypes });
          return;
        }

        const lowerCaseQuery = searchQuery.toLowerCase();
        const filtered = taskTypes.filter(taskType =>
          taskType.name.toLowerCase().includes(lowerCaseQuery) ||
          taskType.memo.toLowerCase().includes(lowerCaseQuery)
        );
        
        set({ filteredTaskTypes: filtered });
      },

      addTaskType: (taskTypeData) => {
        console.log('Adding task type:', taskTypeData); // 디버깅용
        const { taskTypes } = get();
        
        // 새로운 order 값 계산 (기존 최대값 + 1)
        const maxOrder = taskTypes.length > 0 ? Math.max(...taskTypes.map(t => t.order)) : 0;
        
        const newTaskType: TaskType = {
          ...taskTypeData,
          id: ulid(),
          order: maxOrder + 1, // 자동으로 order 설정
          isActive: true, // 새로 생성되는 작업유형은 기본적으로 활성
        };
        
        console.log('New task type:', newTaskType); // 디버깅용
        
        const updatedTaskTypes = [...taskTypes, newTaskType];
        set({ 
          taskTypes: updatedTaskTypes,
          filteredTaskTypes: updatedTaskTypes 
        });
        
        console.log('Updated task types:', updatedTaskTypes); // 디버깅용
        get().filterTaskTypes();
      },

      updateTaskType: (id, updates) => {
        const { taskTypes } = get();
        const updatedTaskTypes = taskTypes.map(taskType =>
          taskType.id === id ? { ...taskType, ...updates } : taskType
        );
        
        set({ 
          taskTypes: updatedTaskTypes,
          filteredTaskTypes: updatedTaskTypes 
        });
        get().filterTaskTypes();
      },

      deleteTaskType: (id) => {
        const { taskTypes } = get();
        const updatedTaskTypes = taskTypes.filter(taskType => taskType.id !== id);
        
        set({ 
          taskTypes: updatedTaskTypes,
          filteredTaskTypes: updatedTaskTypes 
        });
        get().filterTaskTypes();
      },

      getTaskTypeById: (id) => {
        const { taskTypes } = get();
        return taskTypes.find(taskType => taskType.id === id);
      },
    }),
    { name: 'TaskTypeStore' }
  )
);
